import type TomSelect from "tom-select";

export const deselectAllItemsStartedEventName = "deselectAllItemsStarted";
export const deselectAllItemsEndedEventName = "deselectAllItemsEnded";
export const selectAllItemsStartedEventName = "selectAllItemsStarted";
export const selectAllItemsEndedEventName = "selectAllItemsEnded";

export function selectedAutoItemsTomSelectPlugin(
    this: TomSelect,
): void {
    let counterDivElement: HTMLElement;
    let resizeTimeoutId: NodeJS.Timeout;
    let selectAllDispatched: boolean = false;
    let deselectAllDispatched: boolean = false;

    const itemSelectHandler = (selectedItem: HTMLElement): void => {
        const firstElement: HTMLElement = this.control.querySelector("[data-ts-item]") as HTMLElement;
        if (!firstElement || !selectedItem) {
            return;
        }
        const firstElementOffsetTop: number = firstElement.offsetTop;

        if (selectedItem.offsetTop > firstElementOffsetTop || counterDivElement.offsetTop > firstElementOffsetTop) {
            selectedItem.classList.add("d-none");
            handleHiddenItemsNumber();
        }

        if (counterDivElement.offsetTop > firstElementOffsetTop) {
            const visibleElements: NodeListOf<HTMLElement> = this.control.querySelectorAll("[data-ts-item]:not(.d-none)");
            if (visibleElements.length) {
                visibleElements[visibleElements.length - 1].classList.add("d-none");
            }
            handleHiddenItemsNumber();
        }
    };

    const itemDeselectHandler = (selectedItem: HTMLElement): void => {
        const firstElement: HTMLElement = this.control.querySelector("[data-ts-item]") as HTMLElement;
        if (!firstElement || !selectedItem) {
            return;
        }

        if (firstElement.classList.contains("d-none")) {
            firstElement.classList.remove("d-none");
        }
        const firstElementOffsetTop: number = firstElement.offsetTop;

        if (!selectedItem.classList.contains("d-none")) {
            const hiddenItems: HTMLElement[] = Array.from(this.control.querySelectorAll("[data-ts-item].d-none"));
            hiddenItems.forEach((item: HTMLElement): void => {
                item.classList.remove("d-none");
                if (item.offsetTop > firstElementOffsetTop || counterDivElement.offsetTop > firstElementOffsetTop) {
                    item.classList.add("d-none");
                }
            });
        }
        handleHiddenItemsNumber();
    };

    const allItemsHandler = (): void => {
        const selectedItems: HTMLElement[] = Array.from(this.control.querySelectorAll("[data-ts-item]"));
        if (!selectedItems.length) {
            if (counterDivElement.parentNode) {
                counterDivElement.parentNode.removeChild(counterDivElement);
            }
            return;
        } else if (selectedItems[0].classList.contains("d-none")) {
            selectedItems[0].classList.remove("d-none");
        }

        selectedItems.forEach((selectedItem: HTMLElement): void => {
            if (selectedItem.classList.contains("d-none")) {
                selectedItem.classList.remove("d-none");
            }
        });

        const firstElementOffsetTop: number = selectedItems[0].offsetTop;
        selectedItems.forEach((selectedItem: HTMLElement): void => {
            if (selectedItem.offsetTop > firstElementOffsetTop && !selectedItem.classList.contains("d-none")) {
                selectedItem.classList.add("d-none");
            }
        });

        const shownSelected: HTMLElement[] = selectedItems.filter((element: HTMLElement) => !element.classList.contains("d-none"));
        handleHiddenItemsNumber();

        if (counterDivElement.offsetTop > firstElementOffsetTop) {
            shownSelected[shownSelected.length - 1].classList.add("d-none");
            handleHiddenItemsNumber();
        }
    };

    const handleHiddenItemsNumber = (): void => {
        const hiddenItemsNumber: number = this.control.querySelectorAll("[data-ts-item].d-none").length;
        if (hiddenItemsNumber > 0) {
            counterDivElement.textContent = `+${hiddenItemsNumber}`;
            this.control.appendChild(counterDivElement);
        } else if (counterDivElement.parentNode) {
            counterDivElement.parentNode.removeChild(counterDivElement);
        }
    };

    this.on("initialize", (): void => {
        counterDivElement = document.createElement("div");
        counterDivElement.className = "ts-n-items";
        allItemsHandler();
        this.control.addEventListener("selectAllItemsStarted", () => {
            selectAllDispatched = true;
        });
        this.control.addEventListener("selectAllItemsEnded", () => {
            selectAllDispatched = false;
            allItemsHandler();
        });
        this.control.addEventListener("deselectAllItemsStarted", () => {
            deselectAllDispatched = true;
        });
        this.control.addEventListener("deselectAllItemsEnded", () => {
            deselectAllDispatched = false;
            allItemsHandler();
        });

        window.addEventListener("resize", () => {
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            resizeTimeoutId = setTimeout(allItemsHandler, 15);
        });

        const resizeObserver: ResizeObserver = new ResizeObserver((): void => {
            allItemsHandler();
        });
        const firstPreselectedElement: HTMLElement | null = this.control.querySelector("[data-ts-item]");
        if (firstPreselectedElement) {
            resizeObserver.observe(firstPreselectedElement);
        }
    });

    this.on("item_remove", (text: string, item: HTMLElement): void => {
        item.classList.add("deselected");
        if (!deselectAllDispatched) {
            itemDeselectHandler(item);
        }
    });

    this.on("item_add", (text: string, item: HTMLElement): void => {
        item.classList.remove("deselected");
        if (!selectAllDispatched) {
            itemSelectHandler(item);
        }
    });
}
